import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { ACDLError } from '../../models/error.model';
import { IMainApiService } from '../../services/api.service';
import { IApiService } from '../../services/api.service.base';
import { IApiKey, IIntegrationsStore } from './integrations.store';

export interface IIntegrationsService {
  loadApiKeys(): Promise<void | ACDLError>;
  createApiKey(name: string): Promise<void | ACDLError>;
  editApiKey(data: { id: string; name: string; isActive: boolean }): Promise<void | ACDLError>;
  deleteApiKey(apiKeyId: string): Promise<void | ACDLError>;
  isNameUnique(name: string, id?: string): boolean;
  store: IIntegrationsStore;
}

export namespace IIntegrationsService {
  export const $: interfaces.ServiceIdentifier<IIntegrationsService> = Symbol('INTEGRATIONS_SERVICE');
}

@injectable()
export class IntegrationsService implements IIntegrationsService {
  constructor(
    @inject(IIntegrationsStore.$) public readonly store: IIntegrationsStore,
    @inject(IMainApiService.$) private readonly apiService: IApiService
  ) {
    makeObservable(this);
  }

  @action
  async loadApiKeys(): Promise<void | ACDLError> {
    const response = await this.apiService.getAsync<IApiKey[]>('/apiKeys/GetUsersApiKeys');
    if (response instanceof ACDLError) {
      return response;
    }
    this.store.apiKeys.replace(response);
    return Promise.resolve(undefined);
  }

  @action
  async createApiKey(name: string): Promise<void | ACDLError> {
    const response = await this.apiService.postAsync('/apiKeys/Create', { name });
    await this.loadApiKeys();
    if (response instanceof ACDLError) {
      return response;
    }
    return Promise.resolve(undefined);
  }

  @action
  async editApiKey(data: { id: string; name: string; isActive: boolean }): Promise<void | ACDLError> {
    const response = await this.apiService.postAsync('/apiKeys/Edit', data);
    await this.loadApiKeys();
    if (response instanceof ACDLError) {
      return response;
    }
    return Promise.resolve(undefined);
  }

  @action
  async deleteApiKey(apiKeyId: string): Promise<void | ACDLError> {
    const url = '/apiKeys/Delete';
    const response = await this.apiService.postAsync(url, { id: apiKeyId });
    await this.loadApiKeys();
    if (response instanceof ACDLError) {
      return response;
    }
    return Promise.resolve(undefined);
  }

  isNameUnique(name: string, id?: string): boolean {
    return this.store.apiKeys.every((s) => (id && s.id === id) || s.name !== name.trim());
  }
}
